import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Loader2, Search, Send, Bell, CheckCircle2, Download, Circle, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_C = {
  not_sent: 'bg-gray-100 text-gray-500', sent: 'bg-blue-100 text-blue-700', reminded: 'bg-amber-100 text-amber-700',
  partial: 'bg-violet-100 text-violet-700', complete: 'bg-emerald-100 text-emerald-700', imported: 'bg-[#00A79D] text-white',
};
const STATUS_LABEL = { not_sent: 'Not sent', sent: 'Sent', reminded: 'Reminded', partial: 'Partial', complete: 'Complete', imported: 'Imported' };

const QuestionSets = () => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);

  const doSearch = useCallback(async () => {
    const { data } = await axios.get(`${API}/question-sets/suppliers`, { params: { search, limit: 25 } });
    setResults(data.items || []);
  }, [search]);
  useEffect(() => { doSearch(); }, [doSearch]);

  const open = async (id) => {
    const { data } = await axios.get(`${API}/question-sets/${id}`);
    setSel(data);
  };

  const setStatus = async (code, status) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/question-sets/${sel.company_id}/set`, { code, status });
      setSel((p) => ({ ...p, sets: data.sets, next: data.next, imported: data.imported }));
      toast.success(`${code} → ${STATUS_LABEL[status]}`);
      doSearch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); } finally { setBusy(false); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="question-sets-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Question sets</h1>
        <p className="text-gray-500 text-sm mt-1">Sequenced supplier data-acquisition. A supplier receives the next set only when the previous is returned and imported.</p>
      </div>

      <div className="grid lg:grid-cols-[340px_1fr] gap-6">
        <div>
          <div className="flex gap-2 mb-3">
            <Input placeholder="Search suppliers" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="qs-search" />
            <Button variant="outline" onClick={doSearch} className="shrink-0"><Search className="w-4 h-4" /></Button>
          </div>
          <div className="space-y-1.5 max-h-[60vh] overflow-y-auto">
            {results.map((r) => (
              <button key={r.id} onClick={() => open(r.id)} className={`w-full text-left flex items-center justify-between gap-2 border rounded-lg px-3 py-2.5 transition-colors ${sel?.company_id === r.id ? 'border-[#00A79D] bg-[#00A79D]/5' : 'border-[#E5E7EB] hover:border-[#00A79D]/40'}`} data-testid={`qs-supplier-${r.id}`}>
                <span className="text-sm font-medium text-gray-800 truncate">{r.name}</span>
                <Badge variant="secondary" className="text-[10px] shrink-0">{r.imported}/{r.total}</Badge>
              </button>
            ))}
          </div>
        </div>

        <div>
          {!sel ? (
            <div className="border border-dashed border-gray-200 rounded-lg py-20 text-center text-gray-400" data-testid="qs-empty">Select a supplier to manage question sets.</div>
          ) : (
            <Card data-testid="qs-tracker"><CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">{sel.company_name}</h2>
                <Badge className="bg-[#00A79D]/10 text-[#00857D]">{sel.imported}/{sel.total} imported</Badge>
              </div>
              <div className="space-y-3">
                {sel.definitions.map((def) => {
                  const st = sel.sets[def.code] || { status: 'not_sent' };
                  const isNext = sel.next === def.code;
                  return (
                    <div key={def.code} className={`border rounded-lg p-4 ${isNext ? 'border-[#00A79D]' : 'border-[#E5E7EB]'}`} data-testid={`qs-set-${def.code}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold text-gray-900 flex items-center gap-2">{def.code} · {def.name}
                            {isNext && <span className="text-[10px] font-semibold text-[#00A79D] inline-flex items-center gap-0.5"><ArrowRight className="w-3 h-3" /> next to send</span>}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">{def.desc}</p>
                        </div>
                        <Badge className={`${STATUS_C[st.status]} shrink-0`} data-testid={`qs-status-${def.code}`}>{STATUS_LABEL[st.status]}</Badge>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        <Button size="sm" variant="outline" disabled={busy || (st.status === 'not_sent' && !isNext)} onClick={() => setStatus(def.code, 'sent')} className="h-7 text-xs" data-testid={`qs-send-${def.code}`}><Send className="w-3 h-3 mr-1" /> Send</Button>
                        <Button size="sm" variant="outline" disabled={busy} onClick={() => setStatus(def.code, 'reminded')} className="h-7 text-xs" data-testid={`qs-remind-${def.code}`}><Bell className="w-3 h-3 mr-1" /> Remind</Button>
                        <Button size="sm" variant="outline" disabled={busy} onClick={() => setStatus(def.code, 'partial')} className="h-7 text-xs" data-testid={`qs-partial-${def.code}`}><Circle className="w-3 h-3 mr-1" /> Partial</Button>
                        <Button size="sm" variant="outline" disabled={busy} onClick={() => setStatus(def.code, 'complete')} className="h-7 text-xs" data-testid={`qs-complete-${def.code}`}><CheckCircle2 className="w-3 h-3 mr-1" /> Complete</Button>
                        <Button size="sm" disabled={busy} onClick={() => setStatus(def.code, 'imported')} className="h-7 text-xs bg-[#00A79D] hover:bg-[#00857D]" data-testid={`qs-import-${def.code}`}><Download className="w-3 h-3 mr-1" /> Import</Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent></Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionSets;
